/**
 * Phase 13 Step 5 — Pk Heatmap card (Slice Pk Distribution).
 *
 * Fetches the server-rendered PNG from
 * ``/api/geometry-campaigns/{id}/heatmap.png`` as a blob and renders it
 * in an <img> tag via ``URL.createObjectURL``. On every axes / colormap
 * / resolution change, a new blob is fetched and the previous object
 * URL is revoked.
 *
 * User-facing copy uses the defense-professional phrasing
 * "Slice Pk Distribution"; the ``.png`` filename and the backend
 * endpoint keep the plain word `heatmap` because the brief pinned it.
 *
 * When the blob fetch returns 409 (campaign not complete / no slices),
 * we surface the server message in a clear "no data" state instead of
 * an empty broken-image box.
 */
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ImageOff, Loader2 } from "lucide-react";
import geometryCampaigns from "@/api/geometryCampaigns";
import { formatApiError } from "@/lib/formatApiError";

const RESOLUTIONS = ["16x16", "32x32", "64x64", "128x128"];

export default function HeatmapCard({ campaign, registry }) {
    const axesPairs = registry?.axes_pairs || [];
    const colormaps = registry?.colormaps || ["viridis", "plasma", "magma", "cividis"];

    const [axesX, setAxesX] = useState("range_m");
    const [axesY, setAxesY] = useState("bearing_deg");
    const [colormap, setColormap] = useState("viridis");
    const [resolution, setResolution] = useState("32x32");
    const [imgUrl, setImgUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const pairKey = useMemo(
        () => `${axesX}__${axesY}`, [axesX, axesY],
    );
    const canRender = campaign?.status === "complete";

    useEffect(() => {
        if (!canRender || !campaign?.id) {
            setImgUrl(null);
            setErr("");
            return;
        }
        let cancelled = false;
        let objectUrl = null;
        setLoading(true);
        setErr("");
        (async () => {
            try {
                const blob = await geometryCampaigns.heatmapPng(campaign.id, {
                    axes_x: axesX,
                    axes_y: axesY,
                    resolution,
                    colormap,
                });
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setImgUrl(objectUrl);
            } catch (e) {
                if (cancelled) return;
                setImgUrl(null);
                setErr(formatApiError(e, "Heatmap unavailable"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [campaign?.id, canRender, axesX, axesY, resolution, colormap]);

    return (
        <Card data-testid="geom-heatmap-card">
            <CardHeader>
                <CardTitle className="text-base">
                    Pk Heatmap — Slice Pk Distribution
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                        <Label>Axes pair</Label>
                        <Select
                            value={pairKey}
                            onValueChange={(v) => {
                                const [nx, ny] = v.split("__");
                                setAxesX(nx);
                                setAxesY(ny);
                            }}
                        >
                            <SelectTrigger data-testid="geom-heatmap-axes-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {axesPairs.map((p) => (
                                    <SelectItem
                                        key={`${p.x}__${p.y}`}
                                        value={`${p.x}__${p.y}`}
                                    >
                                        {p.label || `${p.x} × ${p.y}`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Colormap</Label>
                        <Select value={colormap} onValueChange={setColormap}>
                            <SelectTrigger data-testid="geom-heatmap-colormap-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {colormaps.map((c) => (
                                    <SelectItem key={c} value={c} className="capitalize">
                                        {c}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Resolution</Label>
                        <Select value={resolution} onValueChange={setResolution}>
                            <SelectTrigger data-testid="geom-heatmap-resolution-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {RESOLUTIONS.map((r) => (
                                    <SelectItem key={r} value={r}>
                                        {r}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {!canRender && (
                    <div
                        className="rounded-md border border-ux-border-1 bg-ux-surface-2/40 p-6 text-center text-sm text-muted-foreground"
                        data-testid="geom-heatmap-pending"
                    >
                        The heatmap renders once the campaign reaches <span className="font-mono">complete</span>.
                    </div>
                )}

                {canRender && loading && (
                    <div
                        className="flex items-center justify-center gap-2 rounded-md border border-ux-border-1 bg-ux-surface-2/40 p-12 text-sm text-muted-foreground"
                        data-testid="geom-heatmap-loading"
                    >
                        <Loader2 className="size-4 animate-spin" />
                        Rendering heatmap…
                    </div>
                )}

                {canRender && !loading && err && (
                    <div
                        className="flex items-center justify-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-6 text-xs text-amber-200"
                        data-testid="geom-heatmap-no-data"
                    >
                        <ImageOff className="size-4" /> {err}
                    </div>
                )}

                {canRender && !loading && !err && imgUrl && (
                    <img
                        src={imgUrl}
                        alt={`Slice Pk distribution for ${axesX} × ${axesY}`}
                        className="w-full max-w-2xl rounded-md border border-ux-border-1"
                        data-testid="geom-heatmap-img"
                    />
                )}
            </CardContent>
        </Card>
    );
}
